import {
  TeliaBreadcrumbs,
  TeliaBreadcrumbsItem,
  TeliaCol,
  TeliaGrid,
  TeliaRow,
  TeliaStyleLink,
} from '@teliads/components/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { urlLanguage } from 'apollo';
import { NavigationLink } from 'components/voca/navigation/navigation-stub/models';
import NavigationStub from 'components/voca/navigation/navigation-stub/NavigationStub';
import { TFunction } from 'i18n';
import { getSlugFromLocationPath } from 'utils/utils';

import './Breadcrumbs.scss';

interface Props {
  title?: string;
  parentPage?: string;
  parentPageUrl?: string;
}

export default function Breadcrumbs({ title, parentPage, parentPageUrl }: Props) {
  const { t }: { t: TFunction } = useTranslation();
  const { pathname } = useLocation();
  const currentUrlLanguage = urlLanguage();

  const navStub = NavigationStub.get(currentUrlLanguage!)!;
  const slug = getSlugFromLocationPath(pathname)!;

  const currentLink = navStub.findLinkBy('slug', decodeURI(slug));
  const breadcrumbItems = buildBreadCrumbs();

  function isLastItem(index: number): boolean {
    return index === breadcrumbItems!.length - 1;
  }

  function formatSlug(slug: string): string {
    return slug.split('-').join(' ');
  }

  function buildBreadCrumbs(): NavigationLink[] | undefined {
    if (currentLink) {
      const flattenLinks = navStub.getFlattenLinks();

      return currentLink.path
        .split('/')
        .map((item) => flattenLinks.find((link) => link.slug === item)!)
        .filter((item) => !!item);
    }
  }

  return (
    <TeliaGrid>
      <TeliaRow>
        <TeliaCol width={12} className="breadcrumbs">
          <TeliaBreadcrumbs>
            <TeliaBreadcrumbsItem data-testid="breadcrumb-item">
              <TeliaStyleLink variant="navigation">
                <Link to={`/${currentUrlLanguage}`}>{t('link_home')}</Link>
              </TeliaStyleLink>
            </TeliaBreadcrumbsItem>

            {breadcrumbItems &&
              !parentPage &&
              breadcrumbItems.map((link: NavigationLink, index) => (
                <Fragment key={`${link.name} + ${index}`}>
                  {link.slug && (
                    <TeliaBreadcrumbsItem
                      data-testid="breadcrumb-item"
                      key={link.id}
                      current={isLastItem(index)}
                    >
                      <TeliaStyleLink variant="navigation">
                        <Link to={link.directPath}>{link.name}</Link>
                      </TeliaStyleLink>
                    </TeliaBreadcrumbsItem>
                  )}
                </Fragment>
              ))}

            {parentPage && title && (
              <>
                {parentPageUrl && (
                  <TeliaBreadcrumbsItem data-testid="breadcrumb-item">
                    <TeliaStyleLink variant="navigation">
                      <Link to={parentPageUrl || pathname.slice(0, -(slug.length + 1))}>
                        {formatSlug(parentPage)}
                      </Link>
                    </TeliaStyleLink>
                  </TeliaBreadcrumbsItem>
                )}
                <TeliaBreadcrumbsItem data-testid="breadcrumb-item" current>
                  <TeliaStyleLink variant="navigation">
                    <Link to={pathname}>{title}</Link>
                  </TeliaStyleLink>
                </TeliaBreadcrumbsItem>
              </>
            )}

            {title && !parentPage && !breadcrumbItems && (
              <TeliaBreadcrumbsItem data-testid="breadcrumb-item" current>
                <TeliaStyleLink variant="navigation">
                  <Link to={pathname}>{title || formatSlug(slug)}</Link>
                </TeliaStyleLink>
              </TeliaBreadcrumbsItem>
            )}
          </TeliaBreadcrumbs>
        </TeliaCol>
      </TeliaRow>
    </TeliaGrid>
  );
}
